import React, { useState } from "react";
import { Wand2, RefreshCw, Flame, Lightbulb, Radar, Dna, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import OfferOutput from "../components/OfferOutput";
import TrendingIdeasPicker from "../components/TrendingIdeasPicker";
import { chatJSON } from "../lib/openai";
import {
  buildOfferIdeasPrompt,
  buildOfferFromIdeaPrompt,
} from "../lib/prompts";
import { gatherIdeationSignals, gatherSignalsForNiche } from "../lib/trends";
import { toast } from "sonner";

export const CreateOffer = ({ apiKey, offerData, setOfferData, onCreateContent }) => {
  const [idea, setIdea] = useState("");
  const [audienceDNA, setAudienceDNA] = useState("");
  const [ideas, setIdeas] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [signals, setSignals] = useState(null);

  const [stage, setStage] = useState("idle"); // idle | trending | ideating | building
  const loading = stage !== "idle";
  const loadingLabel =
    stage === "trending"
      ? "Scanning Reddit + Google Trends + YouTube…"
      : stage === "ideating"
      ? "Distilling 5 emotionally urgent niches…"
      : stage === "building"
      ? "Designing the full asset…"
      : "";

  // Fetches live trends, asks AI for 5 niche ideas grounded in them
  const findTrendingIdeas = async () => {
    try {
      setStage("trending");
      const sigs = await gatherIdeationSignals();
      setSignals(sigs);
      toast.message(
        `Pulled ${sigs.sources.reddit_count} Reddit posts${sigs.sources.google_count ? ` + ${sigs.sources.google_count} Google Trends` : ""}`
      );

      setStage("ideating");
      const { system, user } = buildOfferIdeasPrompt({
        trendsDigest: sigs.digest,
        hint: idea,
        audienceDNA,
      });
      const res = await chatJSON({ apiKey, system, user, temperature: 1.1 });
      if (!Array.isArray(res.ideas) || res.ideas.length === 0) {
        throw new Error("Could not produce ideas");
      }
      setIdeas(res.ideas);
      setSelectedIdx(null);
      setOfferData(null);
      toast.success(`${res.ideas.length} trending niche ideas ready — pick one`);
    } catch (e) {
      toast.error(e.message || "Could not fetch trending ideas");
    } finally {
      setStage("idle");
    }
  };

  // Build a full offer either from a selected idea card OR from the free-text idea input
  const buildFullOffer = async ({ fromIdea, fromText }) => {
    try {
      setStage("trending");
      // If niche-specific, gather targeted signals; else reuse ideation signals if present
      const input =
        fromIdea ||
        (fromText
          ? { niche: fromText, hook_insight: "", product_angle: "", audience_shortline: "" }
          : null);
      if (!input) {
        toast.error("Pick a trending idea or type a niche first");
        setStage("idle");
        return;
      }

      const sigs = await gatherSignalsForNiche({
        niche: input.niche || fromText,
      });
      setSignals(sigs);

      setStage("building");
      const { system, user } = buildOfferFromIdeaPrompt({
        idea: input,
        trendsDigest: sigs.digest,
        audienceDNA,
      });
      const res = await chatJSON({ apiKey, system, user, temperature: 0.7 });
      setOfferData(res);
      toast.success("Offer designed");
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setStage("idle");
    }
  };

  const onSelectIdea = async (i) => {
    setSelectedIdx(i);
    await buildFullOffer({ fromIdea: ideas[i] });
  };

  const onGenerateFromText = async () => {
    if (!idea.trim()) {
      toast.error("Type a niche or tap Find Trending Ideas");
      return;
    }
    setIdeas(null);
    setSelectedIdx(null);
    await buildFullOffer({ fromText: idea.trim() });
  };

  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-950 p-8 sm:p-12">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">
            <Lightbulb className="h-3 w-3" strokeWidth={2} />
            Asset Designer
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-white max-w-3xl leading-[1.05]">
            Find what's <span className="text-amber-400">emotionally urgent</span>. Package it as an asset.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-zinc-400 leading-relaxed">
            Pulls live Reddit + Google Trends + YouTube data — surfaces the real frustrations, identity struggles, and emotional pain people are searching for solutions to right now. Pick one and get the full asset blueprint: audience, language, pricing, ladder, positioning.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-4">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5 sm:p-6">
            <div className="space-y-2 mb-5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Niche or angle hint (optional)
              </Label>
              <Input
                placeholder='e.g. "gut health" or leave empty'
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                data-testid="offer-idea-input"
                className="h-11 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-500"
              />
              <p className="text-[11px] text-zinc-500">
                Leave empty for an emotional-urgency sweep, or drop a category hint.
              </p>
            </div>

            {/* Audience DNA — optional, lightweight */}
            <div className="space-y-2 mb-5 pt-3 mt-3 border-t border-white/5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300/90 flex items-center gap-1.5">
                <Dna className="h-3 w-3" strokeWidth={2.2} />
                Audience DNA <span className="text-zinc-500 normal-case font-normal tracking-normal">· optional</span>
              </Label>
              <Textarea
                placeholder={"Drop creators, channels, subs, or accounts your audience already follows. e.g.\n@huberman, r/ADHD, @malibumakeup, https://youtube.com/@aliabdaal"}
                value={audienceDNA}
                onChange={(e) => setAudienceDNA(e.target.value)}
                data-testid="audience-dna-input"
                className="min-h-[78px] bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-emerald-500 text-[12.5px] leading-relaxed resize-none"
              />
              <p className="text-[10.5px] text-zinc-500 leading-relaxed">
                Used to mirror your audience's vocabulary, humour, identity cues, and emotional triggers across every output.
              </p>
            </div>

            <div className="space-y-2">
              <Button
                onClick={findTrendingIdeas}
                disabled={loading}
                data-testid="find-trending-button"
                className="w-full h-12 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} />
                    {loadingLabel}
                  </>
                ) : (
                  <>
                    <Flame className="mr-2 h-4 w-4" strokeWidth={2} />
                    {ideas ? "Re-Generate 5 Urgent Niches" : "Find 5 Urgent Niches"}
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={onGenerateFromText}
                disabled={loading || !idea.trim()}
                data-testid="generate-offer-button"
                className="w-full h-11 rounded-full border border-white/10 text-zinc-200 hover:text-white hover:bg-zinc-800 disabled:opacity-50"
              >
                <Wand2 className="mr-2 h-4 w-4" strokeWidth={1.8} />
                {offerData ? "Re-Generate asset from my niche" : "Build asset from my niche"}
              </Button>
            </div>
          </div>

          {signals && (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-[11px] text-emerald-200/90 flex items-center gap-2">
              <Radar className="h-3.5 w-3.5" strokeWidth={1.8} />
              {(signals.sources.reddit_count || signals.sources.youtube_count || signals.sources.google_count) ? (
                <>
                  Grounded in {signals.sources.reddit_count} Reddit
                  {signals.sources.youtube_count ? ` · ${signals.sources.youtube_count} YouTube` : ""}
                  {signals.sources.google_count ? ` · ${signals.sources.google_count} Google trends` : ""}
                </>
              ) : (
                <>Grounded in Market Research · Hook Library · Knowledge Base</>
              )}
            </div>
          )}
        </aside>

        <div className="lg:col-span-8 space-y-6">
          {loading && (
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 shimmer rounded" />
                  <div className="h-5 w-48 shimmer rounded" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full shimmer rounded" />
                <div className="h-4 w-5/6 shimmer rounded" />
                <div className="h-4 w-4/6 shimmer rounded" />
              </div>
              <p className="text-xs text-zinc-500 pt-1">{loadingLabel}</p>
            </div>
          )}

          {!loading && ideas && !offerData && (
            <TrendingIdeasPicker
              ideas={ideas}
              selectedIndex={selectedIdx}
              onSelect={onSelectIdea}
            />
          )}

          {!loading && !ideas && !offerData && (
            <div className="rounded-2xl border border-dashed border-white/10 bg-zinc-900/40 p-10 text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
                <Flame className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <p className="mt-4 font-display text-lg text-white">
                Tap "Find 5 Urgent Niches" to start.
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                You'll get 5 emotionally urgent niches sourced from what real people are searching, complaining about, and watching today.
              </p>
            </div>
          )}

          {!loading && offerData && (
            <>
              {ideas && (
                <TrendingIdeasPicker
                  ideas={ideas}
                  selectedIndex={selectedIdx}
                  onSelect={onSelectIdea}
                />
              )}
              <OfferOutput data={offerData} apiKey={apiKey} onUseInContent={onCreateContent} />

              {/* Handoff to Content tab — carries niche, audience DNA, emotional triggers, vocabulary */}
              <div
                data-testid="create-my-content-cta"
                className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-rose-500/5 to-transparent p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              >
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
                    Next step
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed max-w-md">
                    Carry this asset's audience, vocabulary, and emotional
                    triggers straight into the content engine — no re-typing.
                  </p>
                </div>
                <Button
                  type="button"
                  data-testid="create-my-content-button"
                  onClick={() => onCreateContent && onCreateContent({
                    niche: offerData?.niche || idea || "",
                    audience: offerData?.target_audience?.who || offerData?.target_audience?.lifestyle || "",
                    product: offerData?.digital_product?.idea || offerData?.offer_ladder?.low_ticket || "",
                    audienceDNA,
                    pain: offerData?.core_problem?.emotional_frustration || offerData?.core_problem?.pain_point || "",
                    keywords: offerData?.searching_for?.keywords || [],
                    positioning: offerData?.positioning_statement || "",
                  })}
                  className="h-11 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1c1302] font-semibold text-sm px-5 whitespace-nowrap transition-all hover:-translate-y-[1px] shadow-[0_10px_28px_-10px_rgba(245,158,11,0.55)]"
                >
                  Create My Content
                  <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2.5} />
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CreateOffer;
